/** A Lens Space the creator can submit web lenses into (org identity lives on the parent). */
export interface WebLensFolder {
    id: string;
    name: string;
}

export interface WebLensOrganization {
    id: string;
    name: string;
    folders: WebLensFolder[];
}

export type SubmissionCheckState = "pending" | "running" | "pass" | "fail";

export interface SubmissionCheck {
    id: string;
    label: string;
    state: SubmissionCheckState;
    detail?: string;
    /** Per-rule findings listed under a failed row. */
    issues?: string[];
}

export interface WebLensSubmitResult {
    lensId?: string;
    status?: string;
}

export interface WebLensPushResult {
    /** Informational, may be null (proto default) — the preview is ephemeral, not a My Lenses record. */
    previewLensId: string | null;
}

/**
 * The start page's fork: submit a new lens (default), pick an existing one to update, or push a
 * development build to the creator's own Snapchat for preview. Rides the query string
 * (?mode=update / ?mode=push) like the folder choice, so it survives reloads and org switches —
 * absence means create, so pre-existing links keep their meaning.
 */
export const WEB_LENS_MODE_PARAM = "mode";
export const WEB_LENS_CREATE_MODE = "create";
export const WEB_LENS_UPDATE_MODE = "update";
export const WEB_LENS_PUSH_MODE = "push";

export const WEB_LENS_START_MODES = [WEB_LENS_CREATE_MODE, WEB_LENS_UPDATE_MODE, WEB_LENS_PUSH_MODE] as const;
export type WebLensStartMode = (typeof WEB_LENS_START_MODES)[number];

/** Narrows a free-form string (radio value, query param) to a mode, replacing casts at those seams. */
export const isWebLensStartMode = (value: string): value is WebLensStartMode =>
    (WEB_LENS_START_MODES as readonly string[]).includes(value);

// Lens Central's own cap on a lens name; the input enforces it and the counter reports it.
export const NAME_MAX_CHARS = 18;

// Pre-ticket UX checks only — the PUT header caps come from the upload ticket itself
// (the exact values bound into the URL signatures), so drift here can never break an
// upload; a stale precheck just lets GCS be the one to refuse.
export const ZIP_MAX_BYTES = 100 * 1024 * 1024;
export const LOGO_MAX_BYTES = 8 * 1024 * 1024;
// PNG only, matching the rest of the system (Lens Studio icons are icon.png; lens-bundle
// rejects non-PNG at conversion).
export const LOGO_ACCEPTED_TYPES = ["image/png"];
// Mirrors lens-bundle's WebLensConstants.LOGO_MAX_DIMENSION, which enforces it at conversion.
// Pre-checked on the push page (a PNG-header read — see measureWebLensLogo) because there the
// conversion runs inside the push call and its INVALID_LENS_ICON rejection reads dev-flavored;
// elsewhere it is hint text only. Drift is safe: the server stays authoritative.
export const LOGO_MAX_DIMENSION_PX = 4096;
// Guidance only — the package-requirements list and the dropzone's lens-budget meter. Each
// mirrors the lens-bundle bound that enforces it at conversion (named in the trailing comment);
// drift here can only misstate the displayed text, never change what the conversion accepts.
export const PACKAGE_UNCOMPRESSED_MAX_BYTES = 150 * 1024 * 1024; // WebLensConstants.UNCOMPRESSED_MAX_BYTES
// Stated literally in requirementUnzipped (the repo's intl test mock has no formatNumber);
// kept here so the mirror to WebLensConstants.MAX_ENTRY_COUNT stays documented in one place.
export const PACKAGE_MAX_ENTRIES = 20_000; // WebLensConstants.MAX_ENTRY_COUNT
// Preview rules, for the requirements text only — nothing here is enforced by this module. An
// unmarked web lens keeps the platform preview rule (an image, or a video up to
// DURATION_LIMIT_SECS, no minimum); requirementPreviewMedia suggests over 1s. A game-marked one
// takes the Lens Studio submission flow's rule verbatim, GAME_LENS_VIDEO_DURATION_REQUIREMENT
// (5s inclusive to 11s exclusive), and states it with that flow's own 5–10s wording — the Studio
// flow is the source of truth for both the numbers and the copy.

// Push-to-device archive cap — pre-checked so an over-cap zip fails before uploading. Mirrors
// lens-studio-backend's push-to-device.maxLensBundleSizeMiB (50 MiB, checked before download)
// and lens-bundle PushToDeviceValidator.ARCHIVE_SIZE_LIMIT (the same 50 MiB on the built lens).
// Deliberately NOT the publish path's LENS_BUNDLE_MAX_BYTES: a preview accepts builds the
// stricter publish budget would refuse — that headroom is the point of previewing early.
export const PUSH_ARCHIVE_MAX_BYTES = 50 * 1024 * 1024;

// CommunityValidator.MAX_ALLOWED_REGULAR_CONTENT_SIZE: 8MiB of non-ML content per packed bundle.
// Web lenses carry no ML (.dnn) entries, so this is effectively the whole converted lens — and
// only the build output is packed, so the budget is spent by the built subtree alone. (NOT
// WebLensConstants.BUNDLE_MAX_BYTES — that mirrors SanityValidator's 50MB whole-file cap.)
export const LENS_BUNDLE_MAX_BYTES = 8 * 1024 * 1024;
